/**
 * Nauma — Media grouping by colour variant.
 *
 * Dawn natively supports only ONE image swap per variant. This script layers
 * on top of that to show a FULL SET of images per colour, using the colour
 * name already present in each image's alt text (tagged server-side in
 * snippets/product-media-gallery.liquid and snippets/product-media-modal.liquid
 * via data-variant-color).
 *
 * Images whose alt text doesn't match a colour option value are tagged
 * data-variant-color="all" and always stay visible (e.g. size chart).
 *
 * Filters both the main gallery/thumbnails AND the zoom/lightbox modal.
 *
 * Requires: constants.js and pubsub.js loaded earlier (Dawn core assets).
 */
(function () {
  function slugify(value) {
    if (!value) return '';
    return value
      .toString()
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  function getProductWrapper(container) {
    return container.closest('.product') || container.parentElement;
  }

  function nameIsColorOption(name) {
    if (!name) return false;
    const trimmedLower = name.trim().toLowerCase();
    return trimmedLower.indexOf('color') === 0 || trimmedLower.indexOf('colour') === 0;
  }

  function getCurrentColorSlug(productWrapper) {
    if (!productWrapper) return null;

    const checkedInput = Array.prototype.find.call(
      productWrapper.querySelectorAll('input[type="radio"]:checked'),
      (el) => nameIsColorOption(el.name)
    );
    if (checkedInput) return slugify(checkedInput.value);

    const select = Array.prototype.find.call(
      productWrapper.querySelectorAll('select'),
      (el) => nameIsColorOption(el.name)
    );
    if (select) return slugify(select.value);

    return null;
  }

  function updateCounter(mediaGallery) {
    const visibleSlides = mediaGallery.querySelectorAll(
      '.product__media-item[data-variant-color]:not(.nauma-media-hidden)'
    );
    const counterTotal = mediaGallery.querySelector('.slider-counter--total');
    if (counterTotal) counterTotal.textContent = visibleSlides.length;
  }

  function ensureActiveSlideVisible(mediaGallery) {
    const sliderComponent = mediaGallery.querySelector('slider-component');
    if (!sliderComponent) return;

    // Let Dawn recount visible slides so its page math / counter match the
    // filtered set (initPages ignores display:none items).
    if (typeof sliderComponent.resetPages === 'function') sliderComponent.resetPages();

    const activeSlide = sliderComponent.querySelector('.is-active');
    const activeHidden = activeSlide && activeSlide.classList.contains('nauma-media-hidden');
    let target = activeSlide;

    if (!activeSlide || activeHidden) {
      target = sliderComponent.querySelector('.product__media-item:not(.nauma-media-hidden)');
      if (target) {
        sliderComponent.querySelectorAll('.product__media-item.is-active').forEach((el) => {
          el.classList.remove('is-active');
        });
        target.classList.add('is-active');
      }
    }

    // Scroll ONLY the slider track horizontally. scrollIntoView() also
    // scrolls the page vertically on mobile (iOS Safari especially), which
    // made the gallery jump to the top of the screen on colour change.
    if (target && target.parentElement) {
      target.parentElement.scrollTo({ left: target.offsetLeft, behavior: 'instant' });
    }
  }

  // Works on any container with [data-variant-color] children — the main
  // gallery (<media-gallery>) or the zoom modal (<product-modal>).
  function filterContainer(container, colorSlug) {
    if (!colorSlug) return;

    const items = container.querySelectorAll('[data-variant-color]');
    items.forEach((item) => {
      const itemColor = item.getAttribute('data-variant-color');
      const visible = itemColor === colorSlug || itemColor === 'all';
      item.classList.toggle('nauma-media-hidden', !visible);
    });

    if (container.matches('media-gallery')) {
      markFirstVisible(container);
      updateCounter(container);
      ensureActiveSlideVisible(container);
    }
  }

  // Tag the first visible slide so the CSS can give it Dawn's first-slide
  // inset (see .nauma-first-visible in nauma-media-grouping.css).
  function markFirstVisible(mediaGallery) {
    const slides = mediaGallery.querySelectorAll('.product__media-item');
    let marked = false;
    slides.forEach((slide) => {
      const isFirst = !marked && !slide.classList.contains('nauma-media-hidden');
      slide.classList.toggle('nauma-first-visible', isFirst);
      if (isFirst) marked = true;
    });
  }

  function initContainer(container) {
    const productWrapper = getProductWrapper(container);
    const colorSlug = getCurrentColorSlug(productWrapper);
    if (colorSlug) filterContainer(container, colorSlug);
  }

  function allContainers() {
    return document.querySelectorAll('media-gallery, product-modal');
  }

  // Dawn's pubsub.js calls subscribers with the published object directly
  // ({ data: { sectionId, html, variant } }) — there is no event.detail.
  function getPayload(event) {
    return (event && (event.data || (event.detail && event.detail.data))) || null;
  }

  function handleVariantChange(event) {
    const payload = getPayload(event);
    const sectionId = payload && payload.sectionId;
    if (!sectionId) return;

    const mediaGallery = document.getElementById('MediaGallery-' + sectionId);
    const modal = document.getElementById('ProductModal-' + sectionId);
    const targets = [mediaGallery, modal].filter(Boolean);
    if (!targets.length) return;

    const variant = payload.variant;
    if (!variant || !variant.options) return;

    targets.forEach((container) => {
      const knownColors = new Set();
      container.querySelectorAll('[data-variant-color]').forEach((el) => {
        const c = el.getAttribute('data-variant-color');
        if (c && c !== 'all') knownColors.add(c);
      });

      const matchedOption = variant.options.find((optionValue) => knownColors.has(slugify(optionValue)));
      if (matchedOption) {
        filterContainer(container, slugify(matchedOption));
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    allContainers().forEach(initContainer);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }

    // Fallback: react directly to colour swatch clicks, independent of
    // whether Dawn finds a fully matching variant (handles out-of-stock /
    // missing variant combinations gracefully). One listener per product
    // wrapper, filtering every gallery/modal container found within it.
    const seenWrappers = new Set();
    allContainers().forEach((container) => {
      const productWrapper = getProductWrapper(container);
      if (!productWrapper || seenWrappers.has(productWrapper)) return;
      seenWrappers.add(productWrapper);

      productWrapper.addEventListener('change', function (event) {
        const target = event.target;
        if (!target || !nameIsColorOption(target.name)) return;

        const colorSlug = slugify(target.value);
        if (!colorSlug) return;

        productWrapper.querySelectorAll('media-gallery, product-modal').forEach((c) => {
          filterContainer(c, colorSlug);
        });
      });
    });

    // Dawn's product-info.js updateMedia() replaces the zoom modal's
    // .product-media-modal__content innerHTML with fresh, unfiltered markup
    // after every variant fetch. Re-apply the current colour filter whenever
    // that content is swapped out.
    document.querySelectorAll('product-modal').forEach((modal) => {
      const content = modal.querySelector('.product-media-modal__content');
      if (!content) return;
      new MutationObserver(() => initContainer(modal)).observe(content, { childList: true });
    });
  });

  // Hidden colours' images are lazy-loaded, so they only start downloading
  // once a swatch is tapped, leaving a blank frame. After the page has
  // loaded, warm up the FIRST image of each colour so the swap is instant.
  window.addEventListener('load', function () {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      const seen = new Set();
      gallery.querySelectorAll('.product__media-item[data-variant-color]').forEach((slide) => {
        const color = slide.getAttribute('data-variant-color');
        if (seen.has(color)) return;
        seen.add(color);
        const img = slide.querySelector('img[loading="lazy"]');
        if (img) img.loading = 'eager';
      });
    });
  });
  // --- Linked-swatch live variant sync -------------------------------
  // Dawn's variantChange pub/sub event fires whenever Size (or any other
  // option) changes, WITHOUT reloading the page or re-rendering this
  // fieldset. Each linked swatch (rendered by
  // snippets/nauma-linked-color-swatches.liquid) carries a
  // data-nauma-sizes map of {sizeValue: siblingVariantId}; this keeps
  // their hrefs in sync with whatever size is currently selected.
  function syncNaumaLinkedSwatches(variant) {
    if (!variant || !variant.options) return;

    document.querySelectorAll('.nauma-linked-swatch__link').forEach(function (link) {
      const base = link.getAttribute('data-nauma-base');
      if (!base) return;

      const fallback = link.getAttribute('data-nauma-fallback');
      const otherPosition = parseInt(link.getAttribute('data-nauma-other-position'), 10);
      let variantId = fallback;

      if (otherPosition >= 1 && variant.options[otherPosition - 1]) {
        const key = variant.options[otherPosition - 1].toString().trim().toLowerCase();
        try {
          const map = JSON.parse(link.getAttribute('data-nauma-sizes') || '{}');
          if (map[key]) variantId = map[key];
        } catch (e) {
          /* leave variantId as fallback */
        }
      }

      link.href = variantId ? base + '?variant=' + variantId : base;
    });
  }

  function handleNaumaVariantChange(event) {
    const payload = getPayload(event);
    syncNaumaLinkedSwatches(payload && payload.variant);
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, handleNaumaVariantChange);
    }

    // Sync once on load too, in case the page opened on a non-default
    // variant (e.g. arrived here via another linked swatch).
    document.querySelectorAll('variant-selects [data-selected-variant]').forEach(function (script) {
      try {
        syncNaumaLinkedSwatches(JSON.parse(script.textContent));
      } catch (e) {
        /* ignore */
      }
    });
  });
})();
